import type { TableProps } from '@/ui/Table';

export type GroupTableControllerBindings<TTableBindings, TModalsBindings> = {
    tableBindings: TTableBindings;
    modalsBindings: TModalsBindings;
};

export type GroupTableControllerTableBindings<TData> = {
    columns: TableProps<TData>['columns'];
    data: TData[];
    rowKey?: TableProps<TData>['rowKey'];
    tableId?: TableProps<TData>['tableId'];
    tableScope?: TableProps<TData>['tableScope'];
    onSortChanged: NonNullable<TableProps<TData>['onSortChanged']>;
    currentSort: NonNullable<TableProps<TData>['currentSort']>;
    pickedData: string[];
    headerSelectionContent: TableProps<TData>['headerSelectionContent'];
    showHeaderCheckbox: boolean;
    onHeaderCheckboxChange: NonNullable<TableProps<TData>['onHeaderCheckboxChange']>;
    onCheckboxChange: NonNullable<TableProps<TData>['onCheckboxChange']>;
    isHeaderChecked: boolean;
    checkboxData: NonNullable<TableProps<TData>['checkboxData']>;
    headerSecondaryRow: TableProps<TData>['headerSecondaryRow'];
    onRowClick: NonNullable<TableProps<TData>['onRowClick']>;
};

type BuildGroupTableControllerBindingsArgs<
    TTableBindings,
    TAnalysisBindings extends object,
    TGroupEditorBindings extends object,
> = {
    table: TTableBindings;
    analysis: TAnalysisBindings;
    groupEditor: TGroupEditorBindings;
};

export const buildGroupTableControllerBindings = <
    TTableBindings,
    TAnalysisBindings extends object,
    TGroupEditorBindings extends object,
>({
    table,
    analysis,
    groupEditor,
}: BuildGroupTableControllerBindingsArgs<
    TTableBindings,
    TAnalysisBindings,
    TGroupEditorBindings
>): GroupTableControllerBindings<TTableBindings, TAnalysisBindings & TGroupEditorBindings> => ({
    tableBindings: table,
    modalsBindings: {
        ...analysis,
        ...groupEditor,
    },
});

export type BuildDomainGroupTableControllerBindingsArgs<
    TTableBindings,
    TModalsBindings extends object,
    TAnalysisKeys extends keyof TModalsBindings,
> = {
    table: TTableBindings;
    analysis: Pick<TModalsBindings, TAnalysisKeys>;
    groupEditor: Omit<TModalsBindings, TAnalysisKeys>;
};

export const buildDomainGroupTableControllerBindings = <
    TTableBindings,
    TModalsBindings extends object,
    TAnalysisKeys extends keyof TModalsBindings,
>({
    table,
    analysis,
    groupEditor,
}: BuildDomainGroupTableControllerBindingsArgs<
    TTableBindings,
    TModalsBindings,
    TAnalysisKeys
>): GroupTableControllerBindings<TTableBindings, TModalsBindings> => {
    const bindings = buildGroupTableControllerBindings({
        table,
        analysis,
        groupEditor,
    });

    return bindings as GroupTableControllerBindings<TTableBindings, TModalsBindings>;
};
